import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SEARCH_MODEL } from '../../../../../../../components/table/Model/TableModel';
import {SimpleSearchWithId} from '../../../../../../../reduxToolkit/Search/SearchApi';
import TableFunction from '../../../../../../../components/table/TableFunction';
import { TABLES } from '../../../../../../../Apis/Tables';
import {
    EMPTY_VALUES,
    currencyFormat,
    getDateFormat,
    getTimeFormat,
    DATE_TIME_FORMAT_NAME_MODEL
} from '../../../../../../../Apis/functions';
import PeriodDetail from '../SubComponents/PeriodDetail';
import {IconClockTable, IconScheduleTable} from "../../../../../../../components/buttons/IconButtons";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
const PayrollUserHistoryTableApp = ({ id }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [historyResults, setHistoryResults] = useState({ content: [], totalElements: 0 });
    const [modified, setModified] = useState(false);

    const getHistoryData = (page, { searchElement } = {}) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: TABLES.PAYROLL_USER_HISTORY,
            key: searchElement,
            userId: id,
        });
        dispatch(SimpleSearchWithId({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success) setHistoryResults(data);
        });
    };

    const { pagination, rowsPerPage } = TableFunction({
        data: historyResults,
        labelRowsPerPage: 'employees_per_page',
        hasContainer: true,
        fixedPagination: false,
        getData: getHistoryData,
        modified,
        setModified,
        getThead: () => [],
        getTbody: () => ({ body: [], data: [] }),
    });

    const columns = [
        {
            field: 'periodStartDate',
            label: t('period_start_date'),
            // The table pre formats timestamps before calling render, so the raw
            // value is taken from the row to format it here.
            render: (value, row) => {
                return   <>
                    <div className={"d-flex"}>
                        <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getDateFormat(row?.periodStartDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                    </div>
                    <div className={"d-flex"}>
                        <IconClockTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getTimeFormat(row?.periodStartDate)}
                    </div>
                </>
            }
        },
        {
            field: 'periodEndDate',
            label: t('period_end_date'),
            render: (value, row) => {
                return  <>
                    <div className={"d-flex"}>
                        <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getDateFormat(row?.periodEndDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                    </div>
                    <div className={"d-flex"}>
                        <IconClockTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getTimeFormat(row?.periodEndDate)}
                    </div>
                </>
            }
        },
        {
            field: 'calculatedAt',
            label: t('calculated_at'),
            render: (value, row) => {
                return  <>
                    <div className={"d-flex"}>
                        <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getDateFormat(row?.calculatedAt, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                    </div>
                    <div className={"d-flex"}>
                        <IconClockTable className={"mr-8px mb-1 icon-color-general-950"} />
                        {getTimeFormat(row?.calculatedAt)}
                    </div>
                </>
            }
        },
        {
            field: 'baseSalary',
            label: t('base_salary'),
            render: (value, row) => value != null ? `$${currencyFormat(row.baseSalary ?? 0)}` : EMPTY_VALUES.DEFAULT,
        },
        {
            field: 'totalOvertime',
            label: t('total_earnings'),
            render: (value) => value != null ? `$${currencyFormat(value ?? 0)}` : EMPTY_VALUES.DEFAULT,
        },
        {
            field: 'grossSalary',
            label: t('gross_salary'),
            render: (value, row) => value != null ? `$${currencyFormat(row.grossSalary ?? 0)}` : EMPTY_VALUES.DEFAULT,
        },
        {
            field: 'totalDeductions',
            label: t('total_deductions'),
            render: (value) => <span className={"font-color-accent-550 ibl-font-w-600"}>{value != null ? `-$${currencyFormat(value)}` : EMPTY_VALUES.DEFAULT}</span>,
        },
        {
            field: 'netPay',
            label: t('net_pay'),
            render: (value) => value != null ? `$${currencyFormat(value)}` : EMPTY_VALUES.DEFAULT,
        },
    ];

    const handleRowExpand = (row) => {
        if (row?.id !== expandedRowId) setExpandedRowId(row?.id);
    };

    const renderExpandedRow = useCallback(
        ({ row }) =>
            <PeriodDetail row={row} />,
        []
    );

    return {
        t,
        history: historyResults?.content ?? [],
        columns,
        pagination,
        rowsPerPage,
        handleRowExpand,
        renderExpandedRow,
    };
};

export default PayrollUserHistoryTableApp;
